import { CircularProgress, Icon } from "@mui/material";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getInfoEp, loadEpFile, putMarkedNode } from "../../shared/api/request";
import SubscribePage from "../../shared/components/SubscribePage";
import AuthContext from "../../shared/context/TokenContext";
import "../../shared/css/basicStyles.css";
import "../../shared/css/ui.css";
import { BulleV2 } from "../../shared/types/Bubble";
import { Character } from "../../shared/types/Character";
import { Episode } from "../../shared/types/Episode";
import ReaderTopbar from "../components/ReaderTopbar";
import ReaderVisual from "../components/ReaderVisual";
import "../css/Reader.css";

type LoadError = "locked" | "not-found" | "unknown";

const SAVE_PROGRESS_INTERVAL = 3000;

const Reader = () => {
  const ctx = useContext(AuthContext);
  const navigate = useNavigate();
  const { idEpisode, nodeId } = useParams();
  let { state } = useLocation();

  const [isBeneylu, setIsBeneylu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<LoadError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [bubbles, setBubbles] = useState<BulleV2[]>();
  const [characters, setCharacters] = useState<Character[]>([]);
  const [nextEp, setNextEp] = useState<Episode | undefined>();

  const [episode, setEpisode] = useState<Episode | undefined>(undefined);
  const [idSerie, setIdSerie] = useState<number>(-1);
  const [params, setParams] = useState<any>({ isDys: false, fontSize: 1 });
  const returnTo: string = state?.returnTo || "/";

  // Reading progress lives in refs so saving it never re-renders the reader
  const visibleBubbleRef = useRef(-1);
  const lastSavedBubbleRef = useRef(-1);
  const bubblesCountRef = useRef(0);

  const saveLastVisibleBubble = useCallback(async () => {
    const index = visibleBubbleRef.current;
    if (index < 0 || index === lastSavedBubbleRef.current) return;

    lastSavedBubbleRef.current = index;
    await putMarkedNode({
      epId: idEpisode,
      nodeId: index,
      lastNode: bubblesCountRef.current - 1 <= index,
    });
  }, [idEpisode]);

  useEffect(() => {
    const saveIndexBubble = setInterval(saveLastVisibleBubble, SAVE_PROGRESS_INTERVAL);

    return () => {
      clearInterval(saveIndexBubble);
      // Also save when leaving the episode, so progress made in the last seconds is kept
      saveLastVisibleBubble();
    };
  }, [saveLastVisibleBubble]);

  const exitReader = useCallback(() => {
    if (isBeneylu && idSerie > 0) navigate(`/serie/${idSerie}`);
    // The home and collection pages reopen the serie panel when they receive a serieId
    else navigate(returnTo, { state: idSerie > 0 ? { serieId: idSerie } : undefined });
  }, [idSerie, isBeneylu, navigate, returnTo]);

  useEffect(() => {
    if (!state || !state.episode) {
      // Load info Episode
      const load = async () => {
        const epServer = await getInfoEp(idEpisode);
        if (epServer?.episode) setEpisode(epServer.episode);
        if (epServer?.idSerie) setIdSerie(epServer.idSerie);
      };
      load();
    } else {
      setEpisode(state.episode);
      setIdSerie(state.idSerie);
      if (state.params) setParams(state.params);
    }
  }, [idEpisode, state]);

  useEffect(() => {
    let isCancelled = false;

    const loader = async () => {
      setIsLoading(true);
      setLoadError(null);
      visibleBubbleRef.current = -1;
      lastSavedBubbleRef.current = -1;
      if (ctx.isBeneylu) setIsBeneylu(ctx.isBeneylu());

      const data = await loadEpFile(idEpisode, true, true);
      if (isCancelled) return;

      if (data?.bubbles) {
        setBubbles(data.bubbles);
        bubblesCountRef.current = data.bubbles.length;
        if (data.infosEpToSend) setCharacters(data.infosEpToSend);
        setNextEp(data.nextEpToSend);
      } else if (data?.status === 401) setLoadError("locked");
      else if (data?.status === 404) setLoadError("not-found");
      else setLoadError("unknown");

      setIsLoading(false);
      window.scrollTo(0, 0);
    };
    loader();

    return () => {
      isCancelled = true;
    };
  }, [ctx, idEpisode, nodeId, reloadKey]);

  const renderState = (content: React.ReactNode) => (
    <div className="rd">
      <ReaderTopbar episode={episode} onExit={exitReader} />
      <div className="rd-state">{content}</div>
    </div>
  );

  if (isLoading)
    return renderState(
      <div role="status" aria-live="polite">
        <CircularProgress sx={{ color: "#FECF03" }} />
        <p className="rd-state__loading">Chargement de l'épisode…</p>
      </div>,
    );

  if (loadError === "locked" && ctx.isEdifice?.())
    return renderState(
      <div className="rd-state__card">
        <span className="rd-state__icon" aria-hidden="true">
          <Icon>lock</Icon>
        </span>
        <h1 className="rd-state__title">Cet épisode ne t'a pas encore été assigné</h1>
        <p className="rd-state__text">Demande à ton enseignant de te débloquer cet épisode pour le lire.</p>
        <button type="button" className="nbk-btn nbk-btn--primary" onClick={exitReader}>
          Retour à la série
        </button>
      </div>,
    );

  if (loadError === "locked")
    return (
      <div className="rd">
        <ReaderTopbar episode={episode} onExit={exitReader} />
        <SubscribePage />
      </div>
    );

  if (loadError)
    return renderState(
      <div className="rd-state__card" role="alert">
        <span className="rd-state__icon" aria-hidden="true">
          <Icon>{loadError === "not-found" ? "search_off" : "cloud_off"}</Icon>
        </span>
        <h1 className="rd-state__title">
          {loadError === "not-found" ? "Cet épisode est introuvable" : "Impossible de charger l'épisode"}
        </h1>
        <p className="rd-state__text">
          {loadError === "not-found"
            ? "Il a peut-être été retiré. Retourne à la série pour choisir un autre épisode."
            : "Vérifie ta connexion internet puis réessaie."}
        </p>
        <div className="rd-state__actions">
          {loadError === "unknown" ? (
            <button type="button" className="nbk-btn nbk-btn--primary" onClick={() => setReloadKey((k) => k + 1)}>
              <Icon aria-hidden="true">refresh</Icon>
              Réessayer
            </button>
          ) : null}
          <button type="button" className="nbk-btn nbk-btn--ghost" onClick={exitReader}>
            Retour à la série
          </button>
        </div>
      </div>,
    );

  return (
    <ReaderVisual
      episode={episode}
      idSerie={idSerie}
      bubbles={bubbles}
      characters={characters}
      nextEp={nextEp}
      returnTo={returnTo}
      setViewableItem={(_bubble: BulleV2, index: number) => {
        visibleBubbleRef.current = index;
      }}
      params={params}
      onExit={exitReader}
    />
  );
};

export default Reader;
